/**
 * Main application layout with sidebar and header.
 */

import React, { useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { Header } from "./Header";

import { FloatingModelAssistant } from "../models/FloatingModelAssistant";

interface MainLayoutProps {
  pageTitle?: string;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ pageTitle }) => {
  const location = useLocation();
  const isInProjectWizard = location.pathname.startsWith("/projects/new");

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Check localStorage for saved preference
    const saved = localStorage.getItem("sidebar-collapsed");
    return saved ? JSON.parse(saved) : false;
  });

  // Save sidebar state to localStorage
  useEffect(() => {
    localStorage.setItem("sidebar-collapsed", JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  return (
    <div className="min-h-screen bg-grey-50 dark:bg-background quantum-grid-bg">
      {/* Mobile Backdrop */}
      {!sidebarCollapsed && !isInProjectWizard && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[105] md:hidden transition-opacity"
          onClick={() => setSidebarCollapsed(true)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      {/* Main content area */}
      <div
        className={`
          transition-all duration-300
          ${
            isInProjectWizard
              ? sidebarCollapsed
                ? "ml-0 md:ml-16"
                : "ml-0 md:ml-64"
              : sidebarCollapsed
                ? "ml-16"
                : "ml-16 md:ml-64"
          }
        `}
      >
        {/* Header */}
        <Header title={pageTitle} />

        {/* Page content */}
        <main className="min-h-[calc(100vh-4rem)]">
          <Outlet />
        </main>

        {/* Global Floating Assistant */}
        <FloatingModelAssistant />
      </div>
    </div>
  );
};

export default MainLayout;
